import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  AbilityEffect,
  CharacterDeck,
  CampaignDeck,
  GameCard,
  CharacterDrawnCard,
} from "@dndModels";
import { DisablableOption } from "@models";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CardRoller from "@dndComponents/other/CardRoller";
import StringBox from "@components/input/strings/StringBox";

interface Props {
  character: Character;
  spell: CharacterAbility;
  effect: AbilityEffect;
  // char_deck: CharacterDeck;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  shared: boolean;
  onChange: () => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterCardEffect({
  character,
  spell,
  effect,
  // char_deck,
  processing,
  disabled,
  log_it = false,
  shared,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLSpanElement | null>(null);
  const [popoverMode, setPopoverMode] = useState("null");
  const [is_processing, setProcessing] = useState(false);

  // const [deck, setDeck] = useState<CampaignDeck | null>(null);
  const [campaign_deck_id, setCampaignDeckId] = useState("");
  const [card, setCard] = useState<GameCard | null>(null);
  const [card_id, setCardId] = useState("");
  const [chosen_card_id, setChosenCardId] = useState("");
  const [deck_empty, setDeckEmpty] = useState(false);
  const [drawn_cards, setDrawnCards] = useState<CharacterDrawnCard[]>([]);
  const [draw_details, setDrawDetails] = useState("");
 
  const load = useCallback(async () => {
    if (spell instanceof CharacterAbility) {
      const char_item = character.items.find(o => o.true_id === spell.item_id);
      if (char_item instanceof CharacterDeck && char_item.campaign_deck_id !== "") {
        setCampaignDeckId(char_item.campaign_deck_id);
      }
    }
    setLoading(true);
    const res = await char_util.getDrawnCards({ char: character });
    setDrawnCards(res);
    setLoading(false);
  }, [character, spell]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, setFetched, load]);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
  }, [processing, is_processing, setProcessing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id)));

  const renderExtras = () => {
    if (popoverMode === "Choose Card") {
      if (spell instanceof CharacterAbility) {
        const char_item = character.items.find(o => o.true_id === spell.item_id);
        // console.log(char_item);
        if (char_item instanceof CharacterDeck && char_item.deck_item) {
          const deck_item = char_item.deck_item;
          const options = char_item.cards.map(c_id => {
            const c = deck_item.cards.find(o => o.true_id === c_id);
            if (c) {
              const opt: DisablableOption = {
                id: c.true_id, text: c.name
              };
              return opt;
            }
            return null;
          }).filter(o => o !== null) as DisablableOption[];
          return (
            <ModalOrDrawer
              open={ popoverAnchorEl !== null }
              onClose={() => {
                setPopoverAnchorEl(null);
                setPopoverMode("");
                character.subscription_holder.modalClosed();
              }}
            >
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <SelectStringBox
                    name="Choose a Card"
                    options={options}
                    value={chosen_card_id}
                    auto_sort
                    onChange={setChosenCardId}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  <ButtonBox
                    name="Draw Card"
                    disabled={chosen_card_id === ""}
                    onClick={() => {
                      setPopoverMode("Card");
                      setProcessing(true);
                      const new_card_id = chosen_card_id;
                      char_item.cards = char_item.cards.filter(o => o !== new_card_id);
                      setCardId(new_card_id);
                      setChosenCardId("");
                    }}
                  />
                </Grid>
              </Grid>
            </ModalOrDrawer>
          );
        }
      }
    } else {
      return (
        <ModalOrDrawer
          open={ popoverAnchorEl !== null }
          onClose={() => {
            setPopoverAnchorEl(null);
            setPopoverMode("");
            character.subscription_holder.modalClosed();
          }}
        >
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              { renderRoll() }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              { renderSaveDrawnCard() }
            </Grid>
          </Grid>
        </ModalOrDrawer>
      );
    }
    return <></>;
  }

  const renderSaveDrawnCard = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Draw Details" 
            value={draw_details}
            onChange={setDrawDetails}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <ButtonBox  
            name="Save Drawn Card"
            onClick={ async () => {
              const drawn_card = new CharacterDrawnCard();
              drawn_card.char_id = character._id;
              drawn_card.drawn_dt = common_util.get_time_number();  
              drawn_card.card_id = card_id;
              drawn_card.details = draw_details;
              setProcessing(true);
              await api.upsertObject("character_drawn_card", drawn_card);
              setProcessing(false);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const drawCard = () => {
    if (campaign_deck_id !== "") {
      api.getObjects({ data_type: "campaign_deck", filter: { "_id*ObjectIDs": [campaign_deck_id] } }).then(res => {
        if (res.length === 1 && res[0] instanceof CampaignDeck) {
          const deck = res[0];
          let look_at_ids = [...deck.fudged_draw_ids, ...deck.card_ids];
          let c_id = look_at_ids[0];
          let drawn_card = drawn_cards.find(o => o.card_id === c_id);
          while (drawn_card) {
            const using_c_id = c_id;
            look_at_ids = look_at_ids.filter(o => o !== using_c_id);
            c_id = look_at_ids[0];
            drawn_card = drawn_cards.find(o => o.card_id === using_c_id);
          }
          if (c_id === "") {
            setDeckEmpty(true);
          } else {
            api.getFullObject("game_card", c_id).then(res2 => {
              if (res2 instanceof GameCard) {
                const card = res2;
                if (deck.fudged_draw_ids.length === 0 && deck.fudged_for_beneficial && !card.beneficial) {
                  deck.card_ids = deck.card_ids.filter(o => o !== card._id);
                  deck.fudged_draw_ids = deck.fudged_draw_ids.filter(o => o !== card._id);
                  if (card.reshuffle) {
                    deck.drawn_card_ids.push(card._id);
                  }
                  api.updateObject("campaign_deck", deck).then(res2 => {
                    drawCard();
                  });
                } else {
                  setCard(res2);
                  deck.card_ids = deck.card_ids.filter(o => o !== card._id);
                  deck.fudged_draw_ids = deck.fudged_draw_ids.filter(o => o !== card._id);
                  if (card.reshuffle) {
                    deck.drawn_card_ids.push(card._id);
                  }
                  api.updateObject("campaign_deck", deck).then(res2 => {
                    setProcessing(false);
                    if (deck.card_ids.length === 0) {
                      setDeckEmpty(true);
                    }
                  });
                }
              } else if (res2 === null) {
                // It's a card that's been deleted.  Remove it from the deck, and then get the next one.
                deck.card_ids = deck.card_ids.filter(o => o !== c_id);
                deck.drawn_card_ids = deck.drawn_card_ids.filter(o => o !== c_id);
                api.updateObject("campaign_deck", deck).then(res2 => {
                  drawCard();
                });
              } else {
                console.error(res2);
                setProcessing(false);
              }
            });
          }
        } else {
          console.error(res);
        }
        setLoading(false);
      });
    }
  }

  const reshuffle = () => {
    if (campaign_deck_id !== "") {
      api.getObjects({ data_type: "campaign_deck", filter: { "_id*ObjectIDs": [campaign_deck_id] } }).then(res => {
        if (res.length === 1 && res[0] instanceof CampaignDeck) {
          const deck = res[0];
          
          deck.card_ids = [...deck.card_ids,...deck.drawn_card_ids];
          deck.card_ids = common_util.shuffleList(deck.card_ids) as string[];
          deck.drawn_card_ids = [];
          setProcessing(true);
          api.updateObject("campaign_deck", deck).then(res2 => {
            setProcessing(false);
            setDeckEmpty(false);
          });
        } else {
          console.error(res);
        }
      });
    }
  }

  const renderDrawCard = () => {
    if (campaign_deck_id !== "") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox
              name={ deck_empty ? "Deck is Empty" : "Draw a Card" }
              disabled={disabled || processing || !enabled || deck_empty || !api.online }
              onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                setPopoverAnchorEl(event.currentTarget);
                setPopoverMode("Card");
                setProcessing(true);
                drawCard();
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <ButtonBox
              name="Reshuffle"
              disabled={disabled || processing || !enabled || !api.online}
              onClick={reshuffle}
            />
          </Grid>
          { renderExtras() }
        </Grid>
      );
    } else {
      if (spell instanceof CharacterAbility) {
        const char_item = character.items.find(o => o.true_id === spell.item_id);
        // console.log(char_item);
        if (char_item instanceof CharacterDeck) {
          // debugger;
          return (
            <>
              <ButtonBox
                name={ char_item.cards.length === 0 ? "Empty" : `${effect.choose_card ? "Choose" : "Draw"} a Card`}
                disabled={disabled || processing || char_item.cards.length === 0 || !enabled || !api.online}
                onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
                  console.log(spell);
                  console.log(character);
                  console.log(effect);
                  console.log(char_item);
                  setPopoverAnchorEl(event.currentTarget);
                  if (effect.choose_card) {
                    setPopoverMode("Choose Card");
                  } else {
                    setPopoverMode("Card");
                    setProcessing(true);
                    const new_card_id = char_item.cards[0];
                    char_item.cards.splice(0, 1);
                    setCardId(new_card_id);
                  }
                }}
              />
              { renderExtras() }
            </>
          );
        }
      }
    }
    return <></>;
  }

  const renderRoll = () => {
    if (popoverAnchorEl !== null) { 
      if (card) {
        // console.log(card);
        return (
          <CardRoller 
            name="Card"
            char={character}
            card={card}
            onApply={() => {
              onChange();
            }}
          />
        );
      } else if (card_id !== "") {
        if (spell instanceof CharacterAbility) {
          const char_item = character.items.find(o => o.true_id === spell.item_id);
          // console.log(char_item);
          if (char_item instanceof CharacterDeck && char_item.deck_item) {
            const deck_item = char_item.deck_item;
            const c = deck_item.cards.find(o => o.true_id === card_id);
            if (c) {
              return (
                <CardRoller 
                  name="Card"
                  char={character}
                  card={c}
                  onApply={() => {
                    onChange();
                  }}
                />
              );
            }
          }
        }
      }
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return renderDrawCard();
  }
}
